import { Box, Link } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { fetchChessNews, type ChessNewsItem } from "./chess-news.api";

const GOLDEN_RATIO = 1.618_033_988_75;
const TICKER_PIXELS_PER_SECOND = 38.2;

const CHESSLA_TICKER_LINES = [
  "CHESS NEWS @ CHESSLA",
  "YOUR GAMES, MADE USEFUL WITH CHESSLA",
  "WITH NIKOLA, FIND THE MOVE YOU MISSED",
  "WITH CHESSLA, YOUR NEXT GOOD MOVE STARTS WITH THE LAST ONE",
  "ANALYZE THE GAME WITH CHESSLA. KEEP THE LESSON.",
  "WITH NIKOLA, LUCKY BLUNDERS GET A SECOND LOOK",
  "WITH CHESSLA, SMALL PATTERNS BECOME BIGGER RATING",
  "THE BOARD REMEMBERS. CHESSLA HELPS YOU LEARN.",
  "WITH NIKOLA, A QUIET MOVE CAN STILL CHANGE EVERYTHING",
  "NIKOLA HAS SEEN WORSE. PROBABLY.",
  "CHECK THE POSITION, THEN CHECK YOUR HABITS WITH NIKOLA.",
  "ONE GAME CLOSER TO YOUR NEXT BREAKTHROUGH WITH CHESSLA",
  "GOOD CHESS LEAVES CLUES. NIKOLA HELPS YOU READ THEM.",
  "WITH NIKOLA, MAKE THE NEXT MOVE MAKE SENSE",
  "REVIEW THE GAME WITH CHESSLA. IMPROVE THE NEXT ONE.",
  "THE ENGINE CALCULATES. NIKOLA MAKES IT CLEAR.",
];

type TickerEntry = { type: "news"; item: ChessNewsItem } | { type: "chessla"; text: string };

const buildTickerEntries = (items: ChessNewsItem[]): TickerEntry[] => {
  const entries: TickerEntry[] = [{ type: "chessla", text: CHESSLA_TICKER_LINES[0] }];

  items.forEach((item, index) => {
    entries.push({ type: "news", item });
    if ((index + 1) % 4 === 0) {
      entries.push({ type: "chessla", text: CHESSLA_TICKER_LINES[0] });
    } else if ((index + 1) % 5 === 0) {
      const text =
        CHESSLA_TICKER_LINES[1 + (Math.floor((index + 1) * GOLDEN_RATIO) % (CHESSLA_TICKER_LINES.length - 1))];
      entries.push({ type: "chessla", text });
    }
  });

  return entries;
};

export const ChessNewsMarquee = () => {
  const [items, setItems] = useState<ChessNewsItem[]>([]);
  const [tickerDurationSeconds, setTickerDurationSeconds] = useState(205);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const playbackFrameRef = useRef<number | null>(null);

  useEffect(() => {
    void fetchChessNews()
      .then(({ items: newsItems }) => setItems(newsItems))
      .catch(() => undefined);
  }, []);

  useEffect(
    () => () => {
      if (playbackFrameRef.current !== null) cancelAnimationFrame(playbackFrameRef.current);
    },
    [],
  );

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const updateDuration = () => {
      const travelDistance = track.scrollWidth / 2;
      setTickerDurationSeconds(Math.max(120, Math.round(travelDistance / TICKER_PIXELS_PER_SECOND)));
    };

    updateDuration();
    const observer = new ResizeObserver(updateDuration);
    observer.observe(track);
    return () => observer.disconnect();
  }, [items]);

  if (items.length === 0) return null;

  const tickerEntries = buildTickerEntries(items);
  const repeatedEntries = [...tickerEntries, ...tickerEntries];
  const setTickerSpeed = (targetRate: number) => {
    const animation = trackRef.current?.getAnimations()[0];
    if (!animation) return;
    if (playbackFrameRef.current !== null) cancelAnimationFrame(playbackFrameRef.current);

    const initialRate = animation.playbackRate;
    const startedAt = performance.now();
    const duration = 360;
    const update = (now: number) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      const easedProgress = 1 - (1 - progress) ** 3;
      animation.playbackRate = initialRate + (targetRate - initialRate) * easedProgress;
      if (progress < 1) playbackFrameRef.current = requestAnimationFrame(update);
    };
    playbackFrameRef.current = requestAnimationFrame(update);
  };

  return (
    <Box
      onMouseEnter={() => setTickerSpeed(0.1)}
      onMouseLeave={() => setTickerSpeed(1)}
      onFocus={() => setTickerSpeed(0.1)}
      onBlur={() => setTickerSpeed(1)}
      sx={{
        borderTop: "2px solid",
        borderColor: "primary.main",
        bgcolor: "primary.main",
        color: "background.default",
        overflow: "hidden",
        minHeight: 35,
        display: "flex",
        alignItems: "center",
      }}
    >
      <Box sx={{ width: "100%", overflow: "hidden", whiteSpace: "nowrap", px: 1.5 }}>
        <Box
          className="news-track"
          ref={trackRef}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            width: "max-content",
            animation: `chessNewsTicker ${tickerDurationSeconds}s linear infinite`,
            "@keyframes chessNewsTicker": {
              from: { transform: "translateX(0)" },
              to: { transform: "translateX(-50%)" },
            },
          }}
        >
          {repeatedEntries.map((entry, index) => (
            <Box
              key={entry.type === "news" ? `${entry.item.url}-${index}` : `${entry.text}-${index}`}
              component="span"
              sx={{ display: "inline-flex", alignItems: "center" }}
            >
              {entry.type === "news" ? (
                <Link
                  href={entry.item.url}
                  target="_blank"
                  rel="noreferrer"
                  underline="none"
                  sx={{
                    color: "inherit",
                    opacity: 1,
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    textDecorationColor: "currentColor",
                    "&:hover": {
                      textDecoration: "underline",
                      textDecorationColor: "currentColor",
                      textDecorationThickness: "2px",
                      textUnderlineOffset: "3px",
                    },
                  }}
                >
                  {entry.item.title}
                </Link>
              ) : (
                <Box
                  component="span"
                  sx={{
                    color: "inherit",
                    opacity: 1,
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                  }}
                >
                  {entry.text}
                </Box>
              )}
              <Box component="span" sx={{ mx: 1.25, fontSize: "0.8rem", fontWeight: 900 }}>
                •
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};
